import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import {
  authHeaders, fmtBytes, fmtDateTime,
  validateDocFile, docIcon,
  DOC_ALLOWED_EXT,
} from "../profileUtils";
import ConfirmDialog from "../../../../components/ConfirmDialog";
import { TabSpinner, TabError } from "./FamilyTab";
import s from "./DocumentsTab.module.css";

/**
 * Documents tab — mirrors the document section in StudentDetailModal but
 * lives in its own tab component for cleaner separation.
 */
export default function DocumentsTab({ studentId }) {
  const [documents,    setDocuments]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState("");
  const [addLabel,     setAddLabel]     = useState("");
  const [addFile,      setAddFile]      = useState(null);
  const [addFileError, setAddFileError] = useState("");
  const [addBusy,      setAddBusy]      = useState(false);
  const [pendingDelete,setPendingDelete]= useState(null);
  const [deleteBusy,   setDeleteBusy]  = useState(false);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true); setError("");
    fetch(`${API_BASE}/api/students/${studentId}/documents`, { headers: authHeaders() })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(rows => { if (!cancelled) setDocuments(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setError("Could not load documents."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  function handleFilePick(file) {
    setAddFile(file || null);
    if (!file) { setAddFileError(""); return; }
    setAddFileError(validateDocFile(file) || "");
    if (!addLabel.trim()) {
      setAddLabel(file.name.replace(/\.[^.]+$/, ""));
    }
  }

  async function uploadDocument(e) {
    e?.preventDefault?.();
    if (addBusy) return;
    const validation = validateDocFile(addFile);
    if (validation) { setAddFileError(validation); return; }
    const label = (addLabel || addFile.name).trim();
    if (!label) { setAddFileError("Please enter a label."); return; }

    setAddBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", addFile);
      fd.append("label", label);
      const res = await fetch(`${API_BASE}/api/students/${studentId}/documents`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error(`Upload failed (HTTP ${res.status})`);
      const row = await res.json();
      setDocuments(prev => [...prev, row]);
      setAddLabel(""); setAddFile(null); setAddFileError("");
    } catch (err) {
      setError(err?.message || "Upload failed.");
    } finally {
      setAddBusy(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete || deleteBusy) return;
    setDeleteBusy(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/student-documents/${pendingDelete.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Delete failed (HTTP ${res.status})`);
      const goneId = pendingDelete.id;
      setDocuments(prev => prev.filter(d => d.id !== goneId));
      setPendingDelete(null);
    } catch (err) {
      setError(err?.message || "Delete failed.");
    } finally {
      setDeleteBusy(false);
    }
  }

  function downloadDocument(doc) {
    if (!doc?.id) return;
    const url = `${API_BASE}/api/student-documents/${doc.id}/download`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  if (loading) return <TabSpinner label="Loading documents…" />;

  return (
    <div className={s.root}>
      {error && <div className={s.errorBanner}>{error}</div>}

      {/* Document list */}
      <div className={s.listHeader}>
        <span className={s.countText}>
          {documents.length} document{documents.length !== 1 ? "s" : ""} on file
        </span>
      </div>

      {documents.length === 0 ? (
        <div className={s.emptyDocs}>No documents attached yet. Upload one below.</div>
      ) : (
        <ul className={s.docList}>
          {documents.map(d => (
            <li key={d.id} className={s.docRow}>
              <span className={s.docIcon} aria-hidden="true">
                {docIcon(d.contentType, d.originalFilename)}
              </span>
              <div className={s.docMain}>
                <div className={s.docLabel}>{d.label || d.originalFilename || "Document"}</div>
                <div className={s.docMeta}>
                  {d.originalFilename && (
                    <span className={s.docFilename}>{d.originalFilename}</span>
                  )}
                  {d.sizeBytes != null && <span>{fmtBytes(d.sizeBytes)}</span>}
                  {d.uploadedAt && <span>{fmtDateTime(d.uploadedAt)}</span>}
                  {d.uploadedBy && <span>by {d.uploadedBy}</span>}
                </div>
              </div>
              <div className={s.docActions}>
                <button
                  type="button"
                  className={s.downloadBtn}
                  onClick={() => downloadDocument(d)}
                  aria-label={`Download ${d.label || "document"}`}>
                  Download
                </button>
                <button
                  type="button"
                  className={s.deleteBtn}
                  onClick={() => setPendingDelete(d)}
                  disabled={deleteBusy}
                  aria-label={`Delete ${d.label || "document"}`}>
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Upload form */}
      <form className={s.uploadForm} onSubmit={uploadDocument} noValidate>
        <div className={s.uploadTitle}>Add document</div>
        <div className={s.uploadFields}>
          <label className={s.field}>
            <span className={s.fieldLabel}>Label</span>
            <input
              type="text"
              className={s.textInput}
              value={addLabel}
              onChange={e => setAddLabel(e.target.value)}
              placeholder="e.g. Birth Certificate"
              maxLength={120}
              disabled={addBusy}
            />
          </label>
          <label className={s.field}>
            <span className={s.fieldLabel}>File</span>
            <input
              type="file"
              className={s.fileInput}
              accept={DOC_ALLOWED_EXT.join(",")}
              onChange={e => handleFilePick(e.target.files?.[0] || null)}
              disabled={addBusy}
            />
          </label>
        </div>
        {addFile && !addFileError && (
          <div className={s.fileHint}>{addFile.name} · {fmtBytes(addFile.size)}</div>
        )}
        {addFileError && <div className={s.fileError}>{addFileError}</div>}
        <div className={s.uploadFoot}>
          <span className={s.hintMuted}>PDF, JPG, PNG, GIF, WEBP, DOC(X), XLS(X) · max 10 MB</span>
          <button
            type="submit"
            className={s.uploadBtn}
            disabled={addBusy || !addFile || !!addFileError}>
            {addBusy ? "Uploading…" : "+ Add document"}
          </button>
        </div>
      </form>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title="Delete document?"
        description={pendingDelete?.label || pendingDelete?.originalFilename || ""}
        message="The document will be permanently removed. This cannot be undone."
        confirmLabel={deleteBusy ? "Deleting…" : "Delete"}
        cancelLabel="Cancel"
        busy={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleteBusy) setPendingDelete(null); }}
      />
    </div>
  );
}
